import type { CancelablePromise } from "../client"
import { OpenAPI } from "../client"
import { request as __request } from "../client/core/request"

export type InitPurchaseOrderDraftDTO = {
  warehouse_id: string
  contragent_id?: string
  supplier?: string
  description?: string
}

export type PurchaseOrderItemCreateDTO = {
  product_id: string
  qty: number
  price: number
  location_code?: string
}

export type PurchaseOrderItemUpsertDTO = {
  id?: string
  product_id: string
  qty: number
  price: number
  location_code?: string
}

export type PurchaseOrderCreateDTO = {
  warehouse_id?: string
  based_document?: string
  partia?: string
  supplier?: string
  description?: string
  items: PurchaseOrderItemCreateDTO[]
}

export type PurchaseOrderUpdateDTO = {
  warehouse_id?: string | null
  based_document?: string | null
  partia?: string | null
  contragent_id?: string | null
  supplier?: string | null
  description?: string | null
  items: PurchaseOrderItemUpsertDTO[]
}

export type PurchaseOrderListItemDTO = {
  id: string
  number: string
  status: string
  created_at: string
  warehouse_code?: string | null
  warehouse_id?: string | null
  contragent_id?: string | null
  supplier: string
  owner_id: string
  owner_acronym: string
  related_pz_id?: string | null
  related_pz_no?: string
}

export type PurchaseOrderDetailDTO = {
  id: string
  number: string
  status: string
  created_at: string
  updated_at?: string | null
  confirmed_at?: string | null
  cancelled_at?: string | null
  based_document: string
  partia?: string
  contragent_id?: string | null
  supplier: string
  description?: string
  warehouse?: { id: string; code: string; name: string } | null
  owner: { id: string; name: string; acronym: string }
  updated_by?: { id: string; name: string; acronym: string } | null
  confirmed_by?: { id: string; name: string; acronym: string } | null
  cancelled_by?: { id: string; name: string; acronym: string } | null
  realized_items_count: number
  total_items_count: number
  ordered_qty_sum: number
  received_qty_sum: number
  items: Array<{
    id: string
    product: { id: string; sku: string; name: string; unit: string }
    qty: number
    received_qty: number
    remaining_qty: number
    price: number
    location_code: string
    price_netto: number
    price_brutto: number
    value_netto: number
    value_brutto: number
  }>
}

export type PurchaseOrderResponseDTO = {
  id: string
  number: string
  status: string
  created_at: string
  updated_at?: string | null
  confirmed_at?: string | null
}

export type CreatePZFromPurchaseOrderDTO = {
  warehouse_id?: string | null
}

export const PurchaseOrdersService = {
  getAllPurchaseOrders: (
    year?: number,
    month?: number,
  ): CancelablePromise<PurchaseOrderListItemDTO[]> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/purchase-orders/get-all",
      query: { year, month },
    })
  },

  initPurchaseOrderDraft: (
    requestBody: InitPurchaseOrderDraftDTO,
  ): CancelablePromise<PurchaseOrderResponseDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/purchase-orders/drafts",
      body: requestBody,
      mediaType: "application/json",
    })
  },

  getPurchaseOrderByID: (
    id: string,
  ): CancelablePromise<PurchaseOrderDetailDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/purchase-orders/{id}",
      path: { id },
    })
  },

  createPurchaseOrder: (
    requestBody: PurchaseOrderCreateDTO,
  ): CancelablePromise<PurchaseOrderResponseDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/purchase-orders/create",
      body: requestBody,
      mediaType: "application/json",
    })
  },

  updatePurchaseOrderByID: (
    id: string,
    requestBody: PurchaseOrderUpdateDTO,
  ): CancelablePromise<PurchaseOrderResponseDTO> => {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/purchase-orders/{id}",
      path: { id },
      body: requestBody,
      mediaType: "application/json",
    })
  },

  confirmPurchaseOrderByID: (
    id: string,
  ): CancelablePromise<PurchaseOrderResponseDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/purchase-orders/{id}/confirm",
      path: { id },
    })
  },

  reopenPurchaseOrderByID: (
    id: string,
  ): CancelablePromise<PurchaseOrderResponseDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/purchase-orders/{id}/reopen",
      path: { id },
    })
  },

  cancelPurchaseOrderByID: (
    id: string,
  ): CancelablePromise<PurchaseOrderResponseDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/purchase-orders/{id}/cancel",
      path: { id },
    })
  },

  deletePurchaseOrderByID: (id: string): CancelablePromise<void> => {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/purchase-orders/{id}",
      path: { id },
    })
  },

  createPzFromPurchaseOrder: (
    id: string,
    requestBody?: CreatePZFromPurchaseOrderDTO,
  ): CancelablePromise<{ id: string; number: string; status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/purchase-orders/{id}/create-pz",
      path: { id },
      body: requestBody,
      mediaType: "application/json",
    })
  },
}
